/*
  True Gear delivery-cost estimator (product page).

  JS port of Shipping/shipping_calculator.py, the validated reproduction of the
  incumbent Bespoke Shipping app's logic (see shipping-calculator-v0 memory).
  Kept in exact lockstep with the Python module on purpose - if the pricing
  model changes there (the freight rebuild), port the same change here. Only
  the RATES section below should ever need to change.

  Estimates freight for THIS ITEM ALONE, at quantity 1 - the incumbent app
  prices per order, and a product page has no visibility into what else is in
  the customer's cart. Copy in the panel makes that explicit.
*/
(function () {
  "use strict";

  // ---- AU postcode -> state, so the customer only has to type a postcode ----
  // Standard Australia Post postcode ranges (public, non-proprietary data).
  function stateFromPostcode(pcStr) {
    var pc = parseInt(pcStr, 10);
    if (isNaN(pc)) return null;
    if ((pc >= 200 && pc <= 299) || (pc >= 2600 && pc <= 2618) || (pc >= 2900 && pc <= 2920)) return "ACT";
    if ((pc >= 1000 && pc <= 1999) || (pc >= 2000 && pc <= 2599) || (pc >= 2619 && pc <= 2899) || (pc >= 2921 && pc <= 2999)) return "NSW";
    if ((pc >= 3000 && pc <= 3999) || (pc >= 8000 && pc <= 8999)) return "VIC";
    if ((pc >= 4000 && pc <= 4999) || (pc >= 9000 && pc <= 9999)) return "QLD";
    if (pc >= 5000 && pc <= 5999) return "SA";
    if (pc >= 6000 && pc <= 6999) return "WA";
    if (pc >= 7000 && pc <= 7999) return "TAS";
    if (pc >= 800 && pc <= 999) return "NT";
    return null;
  }

  // ---- zones, ported verbatim from resolve_zone() in shipping_calculator.py ----
  var EAST_STATES = { NSW: 1, VIC: 1, QLD: 1, ACT: 1 };
  var SATAS_STATES = { SA: 1, TAS: 1 };
  var WANT_STATES = { WA: 1, NT: 1 };

  function startsWithAny(str, prefixes) {
    for (var i = 0; i < prefixes.length; i++) if (str.indexOf(prefixes[i]) === 0) return true;
    return false;
  }

  function resolveZone(postcode, state) {
    var pc = (postcode || "").trim();
    var st = (state || "").trim().toUpperCase();
    if (!pc || !st) return "OTHER";

    if (startsWithAny(pc, ["48", "49", "57", "64", "65", "66", "67", "77"])) return "QUOTE";
    if (st === "NT" && startsWithAny(pc, ["085", "086", "087", "088"])) return "QUOTE";

    if (EAST_STATES[st] && startsWithAny(pc, ["20", "21", "22", "30", "31", "32", "38", "40", "41", "42"])) return "EAST_METRO";
    if (SATAS_STATES[st] && startsWithAny(pc, ["50", "51", "70"])) return "SATAS_METRO";
    if (WANT_STATES[st] && (startsWithAny(pc, ["60", "61"]) || (st === "NT" && startsWithAny(pc, ["080", "081", "082"])))) return "WANT_METRO";

    if (EAST_STATES[st]) return "EAST_RURAL";
    if (SATAS_STATES[st]) return "SATAS_RURAL";
    if (WANT_STATES[st]) return "WANT_RURAL";
    return "OTHER";
  }

  // ---- product classification, ported from classify_product() / size_tier_from_tags() ----
  var GENERATOR_VENDORS = {
    "Champion Generators": 1, "Hyundai Generators": 1, "Westinghouse Generators": 1, "MaxWatt Generators": 1,
  };

  function sizeTierFromTags(tags) {
    var norm = {};
    (tags || []).forEach(function (t) {
      norm[String(t).toLowerCase().replace(/\s+/g, " ").trim()] = 1;
    });
    if (norm["large package"]) return "large";
    if (norm["medium package"]) return "medium";
    if (norm["small package"]) return "small";
    return null;
  }

  function classifyProduct(vendor, tags) {
    var v = (vendor || "").trim();
    if (GENERATOR_VENDORS[v]) {
      var tier = sizeTierFromTags(tags);
      if (tier === "small") return "GENERATOR_SMALL";
      if (tier === "medium") return "GENERATOR_MEDIUM";
      if (tier === "large") return "GENERATOR_LARGE";
      return "OTHER"; // vendor matches but no size tag - incumbent rule needs both
    }
    if (v === "AquaDeck Pontoons") return "AQUADECK";
    if (v === "Floatables") return "FLOATABLES";
    if (v === "EcoFlow Australia") return "ECOFLOW";
    return "OTHER";
  }

  // ---- RATE TABLE - the only section that should change when the pricing ----
  // ---- model is redesigned. Figures as pasted by Tim, 3 Sep 2026. ----------
  var GENERATOR_RATES = {
    GENERATOR_SMALL: { EAST_METRO: 44, EAST_RURAL: 60, SATAS_METRO: 99, SATAS_RURAL: 60, WANT_METRO: 99, WANT_RURAL: 60 },
    GENERATOR_MEDIUM: { EAST_METRO: 88, EAST_RURAL: 120, SATAS_METRO: 99, SATAS_RURAL: 99, WANT_METRO: 99, WANT_RURAL: 99 },
    GENERATOR_LARGE: { EAST_METRO: 199, EAST_RURAL: 295, SATAS_METRO: 250, SATAS_RURAL: 250, WANT_METRO: 250, WANT_RURAL: 250 },
  };
  var ECOFLOW_FREE_AT_OR_ABOVE = 200;
  var ECOFLOW_UNDER_THRESHOLD_RATE = 8;
  var PICKUP_THRESHOLD = 500;

  // ---- calculator, ported from calculate_shipping() -------------------------
  function calculateShipping(vendor, tags, postcode, orderValue) {
    var state = stateFromPostcode(postcode);
    if (!state) {
      return { error: "Enter a valid Australian postcode." };
    }
    var zone = resolveZone(postcode, state);
    var group = classifyProduct(vendor, tags);
    var pickupAvailable = orderValue >= PICKUP_THRESHOLD;

    // "Call us for a quote" only applies to the free-everywhere catch-all -
    // confirmed against real orders, see shipping-calculator-v0 memory.
    if (zone === "QUOTE" && group === "OTHER") {
      return { quoteOnly: true, pickupAvailable: pickupAvailable };
    }

    if (GENERATOR_RATES[group]) {
      var rate = GENERATOR_RATES[group][zone];
      if (rate === undefined) {
        return { unknown: true, pickupAvailable: pickupAvailable };
      }
      return { rate: rate, pickupAvailable: pickupAvailable };
    }

    if (group === "AQUADECK" || group === "FLOATABLES") {
      return { rate: 0, shipToDepot: true, pickupAvailable: pickupAvailable };
    }

    if (group === "ECOFLOW") {
      if (orderValue < ECOFLOW_FREE_AT_OR_ABOVE) {
        return { rate: ECOFLOW_UNDER_THRESHOLD_RATE, pickupAvailable: pickupAvailable };
      }
      return { rate: 0, pickupAvailable: pickupAvailable };
    }

    return { rate: 0, pickupAvailable: pickupAvailable };
  }

  // ---- DOM wiring -------------------------------------------------------------
  function money(n) {
    return "$" + n.toFixed(2).replace(/\.00$/, "");
  }

  function renderResult(container, result, emailed) {
    var out = container.querySelector("[data-tg-ship-result]");
    if (!out) return;
    var emailedNote = emailed ? "We've emailed a copy of this to you too." : "";
    if (result.error) {
      out.innerHTML = '<p class="tg-ship-calc__err">' + result.error + "</p>";
      return;
    }
    // Main cost line and the follow-up detail (pickup, emailed copy) render
    // as separate paragraphs on purpose (12 Sep 2026, Tim) - one run-on
    // sentence buried the actual dollar figure among the fine print.
    if (result.quoteOnly) {
      out.innerHTML = '<p class="tg-ship-calc__price-line">This is a remote postcode - call us on <a href="tel:0370566732">03 7056 6732</a> for a delivery quote.</p>'
        + (emailedNote ? '<p class="tg-ship-calc__sub-line">' + emailedNote + "</p>" : "");
      return;
    }
    if (result.unknown) {
      out.innerHTML = '<p class="tg-ship-calc__price-line">We don\'t have a set rate for this postcode yet - call us on <a href="tel:0370566732">03 7056 6732</a> to confirm.</p>'
        + (emailedNote ? '<p class="tg-ship-calc__sub-line">' + emailedNote + "</p>" : "");
      return;
    }
    var priceLine = result.shipToDepot
      ? "Ships free to your nearest depot."
      : (result.rate === 0 ? "Free delivery to this postcode." : money(result.rate) + " delivery to this postcode.");
    var subLines = [];
    if (result.pickupAvailable) subLines.push("Local pickup is also available for this order.");
    if (emailedNote) subLines.push(emailedNote);
    var html = '<p class="tg-ship-calc__price-line">' + priceLine + "</p>";
    if (subLines.length) {
      html += '<p class="tg-ship-calc__sub-line">' + subLines.join(" ") + "</p>";
    }
    out.innerHTML = html;
  }

  // ---- Klaviyo event, so a flow can email the estimate to the customer ----
  // Fire-and-forget: klaviyo.js queues identify/track calls even before the
  // real script has finished loading, so no readiness check is needed beyond
  // the object existing at all (it may not, e.g. ad blockers) - guarded below.
  function resultSummary(result) {
    if (result.quoteOnly) return "Remote postcode - call for a quote";
    if (result.unknown) return "No set rate - call to confirm";
    if (result.shipToDepot) return "Free to nearest depot";
    return result.rate === 0 ? "Free" : money(result.rate);
  }

  function sendKlaviyoEvent(email, postcode, vendor, title, price, result) {
    if (!window.klaviyo || !email) return;
    try {
      window.klaviyo.identify({ email: email });
      window.klaviyo.track("Requested Shipping Estimate", {
        Postcode: postcode,
        Vendor: vendor,
        ProductTitle: title,
        ItemPrice: price,
        EstimatedShipping: resultSummary(result),
        PageURL: window.location.href,
      });
    } catch (e) {
      // Klaviyo unavailable (blocked, not yet loaded) - the on-page result
      // still shows, this only skips the follow-up email.
    }
  }

  function init() {
    document.querySelectorAll("[data-tg-ship-calc]").forEach(function (container) {
      var form = container.querySelector("[data-tg-ship-form]");
      var postcodeInput = container.querySelector("[data-tg-ship-postcode]");
      var emailInput = container.querySelector("[data-tg-ship-email]");
      if (!form || !postcodeInput) return;

      var vendor = container.getAttribute("data-vendor") || "";
      var title = container.getAttribute("data-title") || "";
      var tags = [];
      try { tags = JSON.parse(container.getAttribute("data-tags") || "[]"); } catch (e) {}
      var price = parseFloat(container.getAttribute("data-price") || "0") || 0;

      form.addEventListener("submit", function (e) {
        e.preventDefault();
        var result = calculateShipping(vendor, tags, postcodeInput.value, price);
        var hasEmail = !!(emailInput && emailInput.value && window.klaviyo);
        renderResult(container, result, hasEmail);
        if (hasEmail) {
          sendKlaviyoEvent(emailInput.value, postcodeInput.value, vendor, title, price, result);
        }
      });
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // exposed for console/QA testing
  window.__tgShippingCalc = { calculateShipping: calculateShipping, resolveZone: resolveZone, classifyProduct: classifyProduct };
})();
